import type { AuthData, TunaThunkDispatch } from '@tunasong/redux'
import { features, OAuth, profilesApi } from '@tunasong/redux'
import { isHomePath, prefetchHome } from '@tunasong/tunasong-ui'
import { prefetchRouteEntity } from './prefetch-route-entity.js'

/**
 * A session is known: put it in the store and start the route's data (GH #1543, #2903). AuthGuard does this once it
 * has a session; `startStoredSession` does it earlier, from the entry.
 */
export const startSession = (dispatch: TunaThunkDispatch, session: AuthData, pathname: string) => {
  const userId = session.idToken.payload.sub
  /** Both callers only call it for a session with a user */
  if (!userId) {
    return
  }
  dispatch(features.user.actions.setAuth(session))
  dispatch(profilesApi.util.prefetch('loadProfile', { emailOrUserId: userId }, {}))
  // Start the route's entity in parallel with the profile instead of after the app shell (GH #1543)
  prefetchRouteEntity(dispatch, pathname)
  if (isHomePath(pathname)) {
    prefetchHome(dispatch, userId)
  }
}

/**
 * Home's data, started from the entry while the app shell loads (GH #2903). AuthGuard renders inside AppContainer,
 * which waits for every plugin, so a session it establishes starts the first screen's calls only once all the JS has
 * loaded. A stored session that has not expired needs no network to use, so it can start them now.
 *
 * An expired session is left to AuthGuard, which refreshes it and then starts the same calls. AuthGuard sees the
 * signed-in state this sets and does not start the session again.
 */
export const startStoredSession = async (dispatch: TunaThunkDispatch, pathname: string) => {
  if (!isHomePath(pathname)) {
    return
  }
  const session = await OAuth.getSession(false)
  if (!session?.idToken?.payload?.sub || OAuth.isSessionExpired(session)) {
    return
  }
  startSession(dispatch, session, pathname)
}
